import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, PauseCircle, ShieldAlert } from 'lucide-react';
import { isMailboxSpec } from '@esa/shared';
import { api, type TenantConfig } from '../../lib/api.js';
import {
  Badge,
  Button,
  Callout,
  Field,
  Input,
  Metric,
  Panel,
  Spinner,
  Textarea,
} from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';
import { MailboxReadingPanel } from './MailboxReadingPanel.js';

/**
 * Configuration → Mail. The desk's mailbox, outbound caps and the breaker (058).
 *
 * THE SCREEN IS BUILT AROUND ONE QUESTION: is this desk's mail going out, and
 * if not, why? So the status comes first — usage against the limits, and the
 * breaker, loudly, when it has tripped — and the limits themselves second.
 *
 * Every limit shows its default and the platform ceiling. A blank field is
 * "use the default", not zero: the worker refuses zero anyway, and a box that
 * silently meant "no cap" is the failure these caps exist to prevent.
 */

interface Caps {
  perHour: number;
  perDay: number;
  perRecipientPerDay: number;
}

interface Trip {
  id: string;
  trippedAt: string;
  reason: string;
  resetAt: string | null;
  resetByName: string | null;
  resetNote: string | null;
}

interface Status {
  configured: { maxPerHour?: number; maxPerDay?: number; maxPerRecipientPerDay?: number };
  effective: Caps;
  defaults: Caps;
  ceilings: Caps;
  usage: { lastHour: number; lastDay: number };
  breaker: Trip | null;
  recentTrips: Trip[];
  held: number;
  notSent: { id: string; to: string | null; subject: string | null; at: string; reason: string | null }[];
  /** Requesters at or one short of the per-requester limit, today. */
  recipients?: { recipient: string; sent: number; freesAt: string }[];
}

type DraftKey = 'maxPerHour' | 'maxPerDay' | 'maxPerRecipientPerDay';
type Draft = Record<DraftKey, string>;

const LIMITS: { key: DraftKey; cap: keyof Caps; label: string; hint: string }[] = [
  {
    key: 'maxPerHour',
    cap: 'perHour',
    label: 'Emails per hour',
    hint: 'A burst. Past this, mail waits and goes out as the hour frees up.',
  },
  {
    key: 'maxPerDay',
    cap: 'perDay',
    label: 'Emails per day',
    hint: 'A runaway. Reaching this STOPS all mail for this group until someone resets it below.',
  },
  {
    key: 'maxPerRecipientPerDay',
    cap: 'perRecipientPerDay',
    label: 'Emails to one requester per day',
    hint: 'A loop. Past this a message to that person is not sent and is flagged for a person to look at. Staff notifications are not counted.',
  },
];

const draftOf = (c: Status['configured']): Draft => ({
  maxPerHour: c.maxPerHour === undefined ? '' : String(c.maxPerHour),
  maxPerDay: c.maxPerDay === undefined ? '' : String(c.maxPerDay),
  maxPerRecipientPerDay: c.maxPerRecipientPerDay === undefined ? '' : String(c.maxPerRecipientPerDay),
});

/** Null for blank (the default), NaN for anything that is not a whole number. */
function parse(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  return /^\d+$/.test(t) ? Number(t) : Number.NaN;
}

const when = (iso: string) => new Date(iso).toLocaleString();

export function MailTab({ tenant, config }: { tenant: string; config: TenantConfig | undefined }) {
  // The mailbox-reading status, shared with the panel below (same query key).
  const reading = useQuery({
    queryKey: ['admin-mail-intake', tenant],
    queryFn: () => api.get<{ mode: string; lastError: string | null }>(`/api/v1/tenants/${tenant}/admin/mail-intake`),
  });
  const write = useConfigWrite(tenant);
  const savedDesk = config?.settings?.deskEmail ?? '';
  const [deskEmail, setDeskEmail] = useState(savedDesk);
  useEffect(() => setDeskEmail(savedDesk), [savedDesk]);
  const q = useQuery({
    queryKey: ['admin-outbound', tenant],
    queryFn: () => api.get<Status>(`/api/v1/tenants/${tenant}/admin/outbound`),
    enabled: Boolean(tenant),
    // Usage moves while the screen is open, and the breaker is the thing
    // somebody is watching when they are here.
    refetchInterval: 30_000,
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (q.data) setDraft(draftOf(q.data.configured));
  }, [q.data]);

  if (q.isLoading || !draft) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">The mail settings could not be loaded.</p>;

  const s = q.data!;
  const errors: Partial<Record<DraftKey, string>> = {};
  for (const l of LIMITS) {
    const n = parse(draft[l.key]);
    if (n === null) continue;
    if (!Number.isInteger(n) || n < 1) errors[l.key] = 'A whole number, 1 or more';
    else if (n > s.ceilings[l.cap]) errors[l.key] = `At most ${s.ceilings[l.cap]}`;
  }
  const invalid = Object.keys(errors).length > 0;
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(s.configured));

  const save = () => {
    // Sent whole: the settings merge replaces `outbound` as one value, and a
    // blank is omitted so it falls back to the default.
    const outbound: Partial<Record<DraftKey, number>> = {};
    for (const l of LIMITS) {
      const n = parse(draft[l.key]);
      if (n !== null) outbound[l.key] = n;
    }
    void write.mutateAsync({
      method: 'patch',
      path: `/api/v1/tenants/${tenant}/admin/settings`,
      body: { outbound },
      done: 'Mail limits saved',
    });
  };

  const reset = () => {
    void write
      .mutateAsync({
        method: 'post',
        path: `/api/v1/tenants/${tenant}/admin/outbound/breaker/reset`,
        body: { note: note.trim() },
        done: 'Breaker reset — held mail is being sent',
      })
      .then(() => setNote(''));
  };

  const pastTrips = s.recentTrips.filter((t) => t.resetAt !== null);
  const atLimit = (s.recipients ?? []).filter((r) => r.sent >= s.effective.perRecipientPerDay);
  const sendAnyway = (id: string) =>
    void write.mutateAsync({
      method: 'post',
      path: `/api/v1/tenants/${tenant}/admin/outbound/${id}/send-anyway`,
      body: {},
      done: 'Queued — it goes out on the next pass, past the per-requester limit only',
    });

  // Empty is allowed — it falls back to the deployment default.
  const deskEmailBad = deskEmail.trim() !== '' && !isMailboxSpec(deskEmail);
  const saveDesk = () => {
    void write.mutateAsync({
      method: 'patch',
      path: `/api/v1/tenants/${tenant}/admin/settings`,
      body: { deskEmail: deskEmail.trim() },
      done: 'Desk mailbox saved',
    });
  };

  return (
    <div className="space-y-4">
      {s.breaker && (
        <Callout tone="danger" icon={<ShieldAlert />} title="Outbound mail is stopped for this group">
          <p className="text-xs leading-relaxed">
            The breaker tripped at {when(s.breaker.trippedAt)}. Nothing — replies, notifications,
            anything — is being emailed until it is reset.{' '}
            {s.held > 0 && (
              <strong>
                {s.held} message{s.held === 1 ? ' is' : 's are'} waiting.
              </strong>
            )}
          </p>
          <p className="mt-1 font-mono text-2xs text-ink-secondary">{s.breaker.reason}</p>
          <p className="mt-2 text-2xs leading-relaxed text-ink-tertiary">
            Look at the outbox before resetting. A breaker trips because something sent far more
            than usual — often a loop with an auto-responder. Reset once the cause is fixed; the
            waiting mail then goes out within the hourly limit.
          </p>
          <div className="mt-2.5 space-y-2">
            <Field label="Why it is safe to resume" hint="Recorded with the reset.">
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="e.g. Loop with the Payroll auto-responder; responder address now suppressed."
                aria-label="Reason for reset"
              />
            </Field>
            <Button
              variant="danger"
              onClick={reset}
              disabled={note.trim().length < 3}
              loading={write.isPending}
            >
              Reset breaker and resume mail
            </Button>
          </div>
        </Callout>
      )}

      <Panel
        title="Desk mailbox"
        subtitle="This group's own address. Replies to requesters come from it and come back to it."
        action={
          <Button
            onClick={saveDesk}
            disabled={deskEmail.trim() === savedDesk.trim() || deskEmailBad}
            loading={write.isPending}
          >
            Save
          </Button>
        }
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            Mail is never sent TO any group&rsquo;s desk mailbox: it would be filed straight back in
            as a ticket and loop.
          </p>
        }
      >
        <Field
          label="This desk's email address"
          hint="Leave empty to use the deployment default."
          error={deskEmailBad ? 'One address, as "Name <a@b>" or "a@b"' : undefined}
        >
          <Input
            type="text"
            value={deskEmail}
            onChange={(e) => setDeskEmail(e.target.value)}
            placeholder="EAStech Service Desk <eastech@esassoc.com>"
            aria-label="This desk's email address"
          />
        </Field>
        {/* A reading failure is usually about THIS address, so it is said
            here, at the top, not only at the bottom of the page. */}
        {reading.data && reading.data.mode !== 'off' && reading.data.lastError && (
          <Callout tone="danger" icon={<AlertTriangle />} className="mt-3">
            <p className="text-xs">
              <b>Not reading this mailbox.</b> {reading.data.lastError}
            </p>
            <p className="mt-1 text-2xs">
              Sending is separate and unaffected. If the address is right, the mailbox may not exist yet, may be an
              alias or a group rather than a mailbox, or may not be one the platform’s mail-reading app is allowed to
              open. Platform › Mail reading › Check access tests it.
            </p>
          </Callout>
        )}
      </Panel>

      <Panel
        title="Outbound mail limits"
        subtitle="How much email this group may send. Applies to everything the desk sends, however it was queued."
        action={
          <Button onClick={save} disabled={!dirty || invalid} loading={write.isPending}>
            Save
          </Button>
        }
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            Leave a limit blank to use the default. The maximum is set for the whole platform and
            cannot be raised from here. These limits sit where mail is actually sent, so they hold
            whatever queued the message — an agent, a rule, or the intake assistant.
          </p>
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          {/* About SENDING only. It said "mail flowing" while the same
              page reported the mailbox could not be read at all, which read
              as two contradictory verdicts on one desk. */}
          {s.breaker ? (
            <Badge tone="danger" icon={<PauseCircle />}>
              sending stopped
            </Badge>
          ) : (
            <Badge tone="ok" icon={<CheckCircle2 />}>
              sending allowed
            </Badge>
          )}
          {s.held > 0 && (
            <Badge tone="warn" icon={<AlertTriangle />}>
              {s.held} held
            </Badge>
          )}
        </div>

        <div className="mb-4 grid gap-2.5 sm:grid-cols-3">
          <Metric
            label="Sent in the last hour"
            value={`${s.usage.lastHour} / ${s.effective.perHour}`}
            tone={s.usage.lastHour >= s.effective.perHour ? 'warn' : 'default'}
          />
          <Metric
            label="Sent today"
            hint="Last 24 hours, or since the last reset"
            value={`${s.usage.lastDay} / ${s.effective.perDay}`}
            tone={s.breaker ? 'danger' : s.usage.lastDay >= s.effective.perDay * 0.8 ? 'warn' : 'default'}
          />
          {/* THE THIRD LIMIT, with a number. The two tiles above said 0 of 20
              while one address was already blocked at 5 of 5: the limit that
              was actually stopping mail was the only one not on screen. */}
          <Metric
            label="Requesters at their limit"
            hint={`${s.effective.perRecipientPerDay} a day to any one person`}
            value={String(atLimit.length)}
            tone={atLimit.length > 0 ? 'warn' : 'default'}
          />
        </div>
        {(s.recipients ?? []).length > 0 && (
          <ul className="mb-4 space-y-1 rounded-md border border-line-subtle p-2.5 text-2xs">
            {(s.recipients ?? []).map((r) => {
              const full = r.sent >= s.effective.perRecipientPerDay;
              return (
                <li key={r.recipient} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-ink">{r.recipient}</span>
                  <span className={full ? 'text-warn' : 'text-ink-secondary'}>
                    {r.sent} of {s.effective.perRecipientPerDay} today
                  </span>
                  <span className="text-ink-tertiary">
                    {full ? `blocked until ${when(r.freesAt)}` : 'one more and it is blocked'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        <div className="grid gap-2.5 sm:grid-cols-3">
          {LIMITS.map((l) => (
            <Field
              key={l.key}
              label={l.label}
              hint={`${l.hint} Default ${s.defaults[l.cap]}, maximum ${s.ceilings[l.cap]}.`}
              error={errors[l.key]}
            >
              <Input
                type="number"
                min={1}
                max={s.ceilings[l.cap]}
                placeholder={`default ${s.defaults[l.cap]}`}
                value={draft[l.key]}
                onChange={(e) => setDraft({ ...draft, [l.key]: e.target.value })}
                aria-label={l.label}
              />
            </Field>
          ))}
        </div>
      </Panel>

      <Panel
        title="Not sent"
        subtitle="Email from the last 14 days that will not go out, and why. The most recent twenty."
      >
        {s.notSent.length === 0 ? (
          <p className="text-xs text-ink-tertiary">Nothing — every email in the last 14 days was sent or is still waiting.</p>
        ) : (
          <ul className="space-y-2">
            {s.notSent.map((n) => (
              <li key={n.id} className="rounded-md border border-line-subtle p-2.5 text-2xs">
                <div className="flex flex-wrap items-baseline gap-x-2 text-ink-secondary">
                  <span>{when(n.at)}</span>
                  <span className="font-medium text-ink">{n.to ?? '(no recipient)'}</span>
                  {n.subject && <span className="truncate">· {n.subject}</span>}
                </div>
                {n.reason && <div className="mt-0.5 text-ink">{n.reason}</div>}
                {/* The reason says "requeue by hand"; this is the hand. Waives
                    the per-requester limit only, and is recorded. */}
                <div className="mt-1.5">
                  <Button size="xs" variant="secondary" onClick={() => sendAnyway(n.id)} loading={write.isPending}>
                    Send anyway
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {pastTrips.length > 0 && (
        <Panel title="Past breaker trips" subtitle="The most recent ten, and who resumed mail">
          <ul className="space-y-2">
            {pastTrips.map((t) => (
              <li key={t.id} className="rounded-md border border-line-subtle p-2.5 text-2xs">
                <div className="text-ink-secondary">
                  Tripped {when(t.trippedAt)} · reset {when(t.resetAt!)}
                  {t.resetByName ? ` by ${t.resetByName}` : ''}
                </div>
                {t.resetNote && <div className="mt-0.5 text-ink">{t.resetNote}</div>}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* Last: its list of recent mail is the long one, and the limits and
          what did not go out are what someone opens this tab to check. */}
      <MailboxReadingPanel tenant={tenant} />
    </div>
  );
}
